import type { IUserInfo } from "@/account/account-models";
import { EAvatarType } from "@/common/avatar/avatar-helpers";
import { Badge } from "@/common/badge";
import type { EBadgeType } from "@/common/badge/badge-helpers";
import type { IButtonState, IButtonTo } from "@/common/button";
import { EButtonType } from "@/common/button/button-models";
import { LinkConditional } from "@/common/link-conditional/LinkConditional";
import type { IMediaType } from "@/models";
import { DEFAULT_IMAGE_SRC } from "@/utils/images";
import { renderDefinedTrue, renderIf } from "@/utils/react-conditional";
import { exists } from "@/utils/variable-evaluation";
import React from "react";

// Avatar.module.scss's per-type sizing is pre-merged here into one final
// class string per type/slot (rather than stacking base + override utility
// classes) -- avoids relying on Tailwind's generated-CSS source order to
// pick the winner for same-property utilities (e.g. h-10 vs h-25),
// matching the original SCSS cascade outcome exactly. `EAvatarType.Header`
// has no corresponding rule in the original file -- it (and `undefined`)
// fall through to DEFAULT_AVATAR_CLASSES.
const WRAPPER_BASE = "relative inline-block align-middle shrink-0";
const IMAGE_BASE =
  "absolute top-1/2 left-1/2 object-cover object-top -translate-x-1/2 -translate-y-1/2 bg-media-placeholder";
const BADGE_BASE = "absolute bottom-0 left-1/2 transform-[translate(-50%,10%)]";

const DEFAULT_AVATAR_CLASSES = {
  wrapper: `${WRAPPER_BASE} h-header-height w-header-height`,
  avatarImage: `${IMAGE_BASE} rounded-full h-10 w-10`,
  badge: BADGE_BASE,
};

const AVATAR_TYPE_CLASSES: Partial<
  Record<EAvatarType, { wrapper: string; avatarImage: string; badge: string }>
> = {
  [EAvatarType.Boost]: {
    wrapper: `${WRAPPER_BASE} w-25 h-25`,
    avatarImage: `${IMAGE_BASE} rounded-[20px] border-4 border-border-neutral w-25 h-25`,
    badge: BADGE_BASE,
  },
  [EAvatarType.MessengerAllRead]: {
    wrapper: `${WRAPPER_BASE} w-4.5 h-4.5`,
    avatarImage: `${IMAGE_BASE} rounded-full w-4.5 h-4.5`,
    badge: BADGE_BASE,
  },
  [EAvatarType.CompleteProfile]: {
    wrapper: `${WRAPPER_BASE} w-30 h-30`,
    avatarImage: `${IMAGE_BASE} rounded-full w-30 h-30`,
    badge: BADGE_BASE,
  },
  [EAvatarType.AccountMain]: {
    wrapper: `${WRAPPER_BASE} w-15 h-15 mr-0`,
    avatarImage: `${IMAGE_BASE} rounded-full w-15 h-15`,
    badge: BADGE_BASE,
  },
  [EAvatarType.ChatsItem]: {
    wrapper: `${WRAPPER_BASE} w-header-height h-header-height`,
    avatarImage: `${IMAGE_BASE} rounded-full w-header-height h-header-height`,
    badge: BADGE_BASE,
  },
  [EAvatarType.DesktopStickyDiscover]: {
    wrapper: `${WRAPPER_BASE} w-header-height h-header-height`,
    avatarImage: `${IMAGE_BASE} rounded-full w-header-height h-header-height`,
    badge: BADGE_BASE,
  },
  [EAvatarType.DesktopDiscover]: {
    wrapper: `${WRAPPER_BASE} w-32.5 h-32.5`,
    avatarImage: `${IMAGE_BASE} rounded-full w-32.5 h-32.5`,
    badge: BADGE_BASE,
  },
  [EAvatarType.Login]: {
    wrapper: `${WRAPPER_BASE} w-21 h-21 rounded-full border-4 border-surface`,
    avatarImage: `${IMAGE_BASE} rounded-full w-20 h-20`,
    badge: BADGE_BASE,
  },
  [EAvatarType.GetAccess]: {
    wrapper: `${WRAPPER_BASE} w-33.75 h-33.75 rounded-full overflow-hidden`,
    avatarImage: `${IMAGE_BASE} rounded-full w-33.75 h-33.75`,
    badge: BADGE_BASE,
  },
  [EAvatarType.SubscribePopup]: {
    wrapper: `${WRAPPER_BASE} w-27.5 h-27.5 rounded-full overflow-hidden`,
    avatarImage: `${IMAGE_BASE} rounded-full w-27.5 h-27.5`,
    badge: BADGE_BASE,
  },
  [EAvatarType.PaymentPopup]: {
    wrapper: `${WRAPPER_BASE} w-18 h-18 rounded-full overflow-hidden`,
    avatarImage: `${IMAGE_BASE} rounded-full w-18 h-18`,
    badge: BADGE_BASE,
  },
  [EAvatarType.TipSuccessPopup]: {
    wrapper: `${WRAPPER_BASE} w-22.5 h-22.5 rounded-full overflow-hidden`,
    avatarImage: `${IMAGE_BASE} rounded-full w-22.5 h-22.5`,
    badge: BADGE_BASE,
  },
  [EAvatarType.HeaderDesktop]: {
    wrapper: `${WRAPPER_BASE} h-header-height w-header-height`,
    avatarImage: `${IMAGE_BASE} rounded-full h-10 w-10`,
    badge: "absolute bottom-0.75 left-1/2 transform-[translate(-50%,10%)]",
  },
  [EAvatarType.HeaderMobileIcon]: {
    wrapper: `${WRAPPER_BASE} w-5.5 h-5.5 -mt-1`,
    avatarImage: `${IMAGE_BASE} rounded-full w-5.5 h-5.5`,
    badge: BADGE_BASE,
  },
  [EAvatarType.VIPAnnouncement]: {
    wrapper: `${WRAPPER_BASE} w-37.5 h-37.5 rounded-full bg-neutral-bg shadow-[0px_2px_4px_rgba(0,0,0,0.25)]`,
    avatarImage: `${IMAGE_BASE} rounded-full w-35 h-35`,
    badge: BADGE_BASE,
  },
  [EAvatarType.Promo]: {
    wrapper: `${WRAPPER_BASE} w-21.5 h-21.5 rounded-full border-4 border-border-neutral tablet:w-32 tablet:h-32`,
    avatarImage: `${IMAGE_BASE} rounded-full w-19.5 h-19.5 tablet:w-30 tablet:h-30`,
    badge: BADGE_BASE,
  },
  [EAvatarType.MyPurchase]: {
    wrapper: `${WRAPPER_BASE} w-10 h-10 rounded-full overflow-hidden`,
    avatarImage: `${IMAGE_BASE} rounded-full w-10 h-10`,
    badge: BADGE_BASE,
  },
  [EAvatarType.UnblockUser]: {
    wrapper: `${WRAPPER_BASE} w-25 h-25`,
    avatarImage: `${IMAGE_BASE} rounded-full w-25 h-25`,
    badge: BADGE_BASE,
  },
};

export const Avatar = React.memo(function Avatar(props: {
  isDisabled?: boolean;
  isLoading?: boolean;
  avatar?: IMediaType;
  badge?: EBadgeType;
  type?: EAvatarType;
  to?: IButtonTo | null;
  state?: IButtonState;
  user?: IUserInfo;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onBadgeClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  children?: React.ReactNode;
}) {
  const [isError, setIsError] = React.useState(false);

  React.useEffect(() => {
    setIsError(false);
  }, [props.avatar?.th]);

  const avatarPath = (): string | null => {
    if (exists(props.avatar)) {
      return props.avatar!.th;
    }
    return null;
  };

  const handleImageError = () => {
    setIsError(true);
  };

  const avatarClasses =
    (props.type && AVATAR_TYPE_CLASSES[props.type]) || DEFAULT_AVATAR_CLASSES;

  const renderAvatarImage = () => {
    return (
      <LinkConditional
        isDisabled={!!props.isDisabled}
        onClick={props.onClick}
        to={props.to}
        state={props.state}
        type={EButtonType.Icon}
      >
        {renderIf(exists(avatarPath()) && !isError, {
          ifTrue: () => (
            <img
              className={avatarClasses.avatarImage}
              src={avatarPath()!}
              alt=""
              loading="lazy"
              onError={handleImageError}
            />
          ),
          ifFalse: () => (
            <img
              className={avatarClasses.avatarImage}
              src={DEFAULT_IMAGE_SRC}
              alt=""
              loading="lazy"
            />
          ),
        })}
      </LinkConditional>
    );
  };

  return (
    <div className={avatarClasses.wrapper}>
      {renderAvatarImage()}
      {renderDefinedTrue(props.badge, () => (
        <div className={avatarClasses.badge} onClick={props.onBadgeClick}>
          <Badge type={props.badge} />
        </div>
      ))}

      {props.children}
    </div>
  );
});
